import React from "react";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { adminPost } from "@/lib/admin-api";

interface PeriodSummary {
  start: string;
  end: string;
  donorId: string | null;
  paymentsCount: number;
  totalUsd: number;
  byCategory: Record<string, number>;
  byWishItem: Record<string, { count: number; total: number }>;
  namedDonors: Record<string, { displayName: string; total: number; count: number }>;
}

interface PaymentRow {
  id: string;
  donorEmail: string;
  amountUsd: number;
  createdAt: string;
  metadata?: { dpfWishId?: string };
}

interface GenerateResponse {
  period: PeriodSummary;
  payments: PaymentRow[];
}

function StatementsAdminInner() {
  const [donorId, setDonorId] = React.useState<string>("");
  const [start, setStart] = React.useState<string>("2026-01-01");
  const [end, setEnd] = React.useState<string>(new Date().toISOString().slice(0, 10));
  const [data, setData] = React.useState<GenerateResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onGenerate(saveSnapshot = false) {
    setLoading(true);
    setError(null);
    try {
      const res = await adminPost<GenerateResponse>("/api/admin/statements/generate", {
        donorId: donorId.trim() || undefined,
        startIso: `${start}T00:00:00Z`,
        endIso: `${end}T23:59:59Z`,
        saveSnapshot,
      });
      setData(res);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageShell title="Statements · Admin" status="admin" width="wide">
      <Seo title="Statements admin" description="" path="/admin/statements" noindex />
      <AdminTopNav />

      <h2 className="font-serif text-xl text-ink mb-4">Generate statement</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1" htmlFor="d">
            Donor (email or blank for all)
          </label>
          <input
            id="d"
            value={donorId}
            onChange={(e) => setDonorId(e.target.value)}
            placeholder="ada@example.org"
            className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1" htmlFor="s">
            Start
          </label>
          <input
            id="s"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1" htmlFor="e">
            End
          </label>
          <input
            id="e"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
          />
        </div>
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => onGenerate(false)}
            disabled={loading}
            className="bg-ink text-paper px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-ink-2 disabled:opacity-60"
          >
            {loading ? "Computing…" : "Generate"}
          </button>
          <button
            type="button"
            onClick={() => onGenerate(true)}
            disabled={loading}
            className="border border-ink text-ink px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-paper-2 disabled:opacity-60"
          >
            Save snapshot
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
          {error}
        </div>
      )}

      {data && (
        <div className="space-y-8">
          <section>
            <h3 className="font-serif text-lg text-ink mb-3">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-hairline bg-paper-2 p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1">Total</div>
                <div className="font-mono text-2xl text-ink">
                  US$
                  {data.period.totalUsd.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
              <div className="border border-hairline bg-paper-2 p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1">Gifts</div>
                <div className="font-mono text-2xl text-ink">{data.period.paymentsCount}</div>
              </div>
              <div className="border border-hairline bg-paper-2 p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1">Period</div>
                <div className="font-mono text-xs text-ink">
                  {data.period.start.slice(0, 10)} → {data.period.end.slice(0, 10)}
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-serif text-lg text-ink mb-3">By category</h3>
            <ul className="border border-hairline divide-y divide-hairline">
              {Object.entries(data.period.byCategory).map(([k, v]) => (
                <li key={k} className="px-4 py-3 flex justify-between font-mono text-sm">
                  <span className="text-ink-3 uppercase tracking-widest text-[10px]">{k}</span>
                  <span className="text-ink">
                    US$
                    {v.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="font-serif text-lg text-ink mb-3">Named supporters</h3>
            {Object.keys(data.period.namedDonors).length === 0 ? (
              <p className="font-serif text-sm text-ink-2">
                No named donors in this period.
              </p>
            ) : (
              <ul className="border border-hairline divide-y divide-hairline">
                {Object.entries(data.period.namedDonors).map(([email, d]) => (
                  <li key={email} className="px-4 py-3 flex justify-between text-sm">
                    <span>
                      {d.displayName}{" "}
                      <span className="font-mono text-[10px] text-ink-3">({d.count} gifts)</span>
                    </span>
                    <span className="font-mono text-ink">
                      US$
                      {d.total.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="font-serif text-lg text-ink mb-3">All payments</h3>
            {data.payments.length === 0 ? (
              <p className="font-serif text-sm text-ink-2">No payments in this period.</p>
            ) : (
              <div className="border border-hairline overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-paper-2 text-[10px] font-mono uppercase tracking-widest text-ink-3">
                    <tr>
                      <th className="text-left px-4 py-3 font-bold">Date</th>
                      <th className="text-left px-4 py-3 font-bold">Donor</th>
                      <th className="text-left px-4 py-3 font-bold">Wish</th>
                      <th className="text-right px-4 py-3 font-bold">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline text-ink-2">
                    {data.payments.map((p) => (
                      <tr key={p.id}>
                        <td className="px-4 py-3 font-mono text-xs">
                          {new Date(p.createdAt).toLocaleDateString("en-GB")}
                        </td>
                        <td className="px-4 py-3 text-sm">{p.donorEmail}</td>
                        <td className="px-4 py-3 text-sm">{p.metadata?.dpfWishId || "general"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-right">
                          US${p.amountUsd.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </PageShell>
  );
}

export function StatementsAdmin() {
  return (
    <AdminGuard allow={["admin"]}>
      <StatementsAdminInner />
    </AdminGuard>
  );
}
